import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

type Props = {
    src: string;                    // mp4 หรือ YouTube URL
    height?: number | string;       // เช่น 400, "480px", "60vh"
    aspectRatio?: string;           // เช่น "16 / 9" (ถ้าระบุ height แล้ว จะไม่ใช้ค่านี้)
};

const VideoPlayer: React.FC<Props> = ({ src, height = "100%", aspectRatio = "16 / 9" }) => {
    const { t } = useTranslation();
    const isMP4 = src.toLowerCase().includes(".mp4");
    const isYouTube = src.includes("youtube.com") || src.includes("youtu.be");

    // --- สร้างกล่องห่อที่มีขนาดแน่นอน ---
    const wrapperSx = {
        position: "relative" as const,
        width: "100%",
        ...(height ? { height } : { aspectRatio }), // ต้องมีอันใดอันหนึ่ง
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
        overflow: "hidden",
    };

    // --- แปลง YouTube URL เป็น embed ---
    let embedUrl: string | null = null;
    if (isYouTube) {
        try {
            const u = new URL(src);
            let videoId: string | null = null;

            if (u.hostname.includes("youtube.com")) {
                videoId = u.searchParams.get("v");
            } else if (u.hostname.includes("youtu.be")) {
                // /VIDEO_ID อาจมี ?si=... ต่อท้าย ให้ตัดออก
                videoId = u.pathname.replace("/", "");
            }

            if (videoId) {
                embedUrl = `https://www.youtube.com/embed/${videoId}`;
            }
        } catch (e) {
            console.error("Invalid YouTube URL:", e);
        }
    }

    if (isMP4) {
        return (
            <Box sx={wrapperSx}>
                <video
                    src={src}
                    controls
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                />
            </Box>
        );
    }

    if (embedUrl) {
        return (
            <Box sx={wrapperSx}>
                <iframe
                    src={embedUrl}
                    title="YouTube video player"
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                />
            </Box>
        );
    }

    return (
        <Box className="w-full" sx={{ p: 2, bgcolor: "slategray", borderRadius: 2 }}>
            <Typography>{t("ไม่รองรับลิงก์นี้")}</Typography>
        </Box>
    );
};

export default VideoPlayer;
